<template>
  <div>
    <form @submit.prevent="save">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ isEditing ? 'Edit Shipping Adjustment' : 'Add Shipping Adjustment' }}</p>
        </header>
        <section class="modal-card-body">
          <div v-if="error" class="mb-md">
            <b-message type="is-danger">{{ error }}</b-message>
          </div>
          <!-- Shipping Method -->
          <div class="mb-md">
            <b-field
              label="Shipping Method"
              :type="('shipping_method' in errors) ? 'is-danger' : ''"
              :message="('shipping_method' in errors) ? errors['shipping_method'][0] : ''">
              <b-select v-model="shippingMethod" placeholder="Select a shipping method" expanded size="is-medium">
                <optgroup v-for="account in connection.shipping_accounts" :key="account.id" :label="account.carrier.name + ' — ' + account.nickname">
                  <option v-for="method in account.shipping_methods" :key="method.id" :value="method">
                    {{ method.name }}
                  </option>
                </optgroup>
              </b-select>
            </b-field>
          </div>
          <!-- Adjustment Code -->
          <div class="mb-md">
            <b-field
              label="Adjustment Code"
              :type="('adjustment_code' in errors) ? 'is-danger' : ''"
              :message="('adjustment_code' in errors) ? errors['adjustment_code'][0] : ''">
              <b-select v-model="adjustmentCode" placeholder="Select an adjustment code" expanded size="is-medium" :loading="loadingCodes">
                <option v-for="code in adjustmentCodes" :key="code.code" :value="code">
                  {{ code.description }}
                </option>
              </b-select>
            </b-field>
          </div>
          <!-- Amount Type + Amount -->
          <div class="columns mb-md">
            <div class="column">
              <b-field
                label="Adjustment Amount Type"
                :type="('adjustment_amount_type' in errors) ? 'is-danger' : ''"
                :message="('adjustment_amount_type' in errors) ? errors['adjustment_amount_type'][0] : ''">
                <b-select v-model="adjustmentAmountType" expanded size="is-medium">
                  <option value="flat">Flat</option>
                  <option value="percent">Percent</option>
                </b-select>
              </b-field>
            </div>
            <div class="column">
              <b-field
                label="Adjustment Amount"
                :type="('adjustment_amount' in errors) ? 'is-danger' : ''"
                :message="('adjustment_amount' in errors) ? errors['adjustment_amount'][0] : ''">
                <b-input
                  v-model="adjustmentAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  size="is-medium"
                  expanded
                ></b-input>
              </b-field>
            </div>
          </div>
          <!-- Description -->
          <div class="mb-md">
            <b-field
              label="Description"
              :type="('description' in errors) ? 'is-danger' : ''"
              :message="('description' in errors) ? errors['description'][0] : ''">
              <b-input
                v-model="description"
                type="text"
                size="is-medium"
                expanded
              ></b-input>
            </b-field>
          </div>
        </section>
        <footer class="modal-card-foot space-between">
          <b-button @click="$parent.close()">Close</b-button>
          <b-button
            type="is-primary"
            native-type="submit"
            :loading="saving"
            :disabled="!ready || saving">
            {{ isEditing ? 'Update Adjustment' : 'Save Adjustment' }}
          </b-button>
        </footer>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  computed: {
    ...mapGetters(['context']),
    isEditing () {
      return this.adjustment !== null && this.adjustment !== undefined
    },
    endpoint () {
      return this.context.route + '/connections/' + this.connection.id + '/'
    },
    codesEndpoint () {
      return this.context.route + '/connections/adjustment-codes/'
    },
    ready () {
      return this.shippingMethod !== null &&
        this.adjustmentCode !== null &&
        this.adjustmentAmountType !== null &&
        this.adjustmentAmount !== null &&
        this.adjustmentAmount !== ''
    },
    body () {
      const record = {
        shipping_method: { id: this.shippingMethod.id },
        adjustment_code: { code: this.adjustmentCode.code },
        adjustment_amount_type: this.adjustmentAmountType,
        adjustment_amount: this.adjustmentAmount,
        description: this.description
      }
      if (this.isEditing) {
        record.id = this.adjustment.id
      }
      return { connection_service_level_adjustments: [record] }
    }
  },
  data () {
    return {
      shippingMethod: null,
      adjustmentCode: null,
      adjustmentAmountType: 'flat',
      adjustmentAmount: null,
      description: '',
      adjustmentCodes: [],
      loadingCodes: false,
      saving: false,
      error: null,
      errors: {}
    }
  },
  methods: {
    loadCodes () {
      this.loadingCodes = true
      this.$http.get(this.codesEndpoint).then(response => {
        this.adjustmentCodes = response.data
        if (this.isEditing) {
          this.adjustmentCode = this.adjustmentCodes.find(c => c.code === this.adjustment.adjustment_code.code) || null
        }
      }).finally(() => {
        this.loadingCodes = false
      })
    },
    prefill () {
      const adj = this.adjustment
      // Find the matching shipping method object from connection.shipping_accounts
      for (const account of this.connection.shipping_accounts) {
        const match = account.shipping_methods.find(m => m.id === adj.shipping_method.id)
        if (match) {
          this.shippingMethod = match
          break
        }
      }
      this.adjustmentAmountType = adj.adjustment_amount_type
      this.adjustmentAmount = adj.adjustment_amount
      this.description = adj.description
    },
    save () {
      this.saving = true
      this.error = null
      this.errors = {}
      this.$http.patch(this.endpoint, this.body).then(() => {
        this.$buefy.toast.open({ type: 'is-success', message: this.isEditing ? 'Adjustment updated!' : 'Adjustment saved!' })
        this.$emit('saved:adjustment')
        this.$parent.close()
      }).catch(error => {
        if (error.response && error.response.data) {
          if ('non_field_errors' in error.response.data) {
            this.error = error.response.data.non_field_errors[0]
          } else {
            this.errors = error.response.data
          }
        } else {
          this.error = 'An unexpected error occurred.'
        }
      }).finally(() => {
        this.saving = false
      })
    }
  },
  mounted () {
    if (this.isEditing) {
      this.prefill()
    }
    this.loadCodes()
  },
  props: ['connection', 'adjustment']
}
</script>
